import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileUploadZone } from '@/components/file-processor/file-upload-zone';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { PatternRecognitionService } from '@/services/pattern-recognition';
import { HistoricalAnalysisDialog } from './historical-analysis-dialog';
import { FocusedReviewMode } from '@/components/review/focused-review-mode';
import OnboardingErrorBoundary from '../error-boundaries/OnboardingErrorBoundary';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  completed: boolean;
  required: boolean;
}

interface OrganizationData {
  name: string;
  address: string;
  phone: string;
  email: string;
  vatNumber: string;
  businessType: string;
  industry?: string;
  companySize?: string;
  canton?: string;
}

interface MandateData {
  clientName: string;
  clientAddress: string;
  accountingPeriod: string;
  vatReporting: string;
  specialRequirements: string;
}

interface UploadFunctionResponse {
  success: boolean;
  uploadId?: string;
  message?: string;
  processed?: number;
  averageConfidence?: number;
  highConfidenceCount?: number;
  automationRate?: number;
  processingTimeMs?: number;
  error?: string;
}

export const StructuredOnboardingFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [organizationData, setOrganizationData] = useState<OrganizationData>({
    name: '',
    address: '',
    phone: '',
    email: '',
    vatNumber: '',
    businessType: '',
    industry: '',
    companySize: '',
    canton: ''
  });
  const [mandateData, setMandateData] = useState<MandateData>({
    clientName: '',
    clientAddress: '',
    accountingPeriod: 'monthly',
    vatReporting: 'quarterly',
    specialRequirements: ''
  });
  const [chartOfAccountsFile, setChartOfAccountsFile] = useState<File | null>(null);
  const [historicalDataFiles, setHistoricalDataFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [reviewTransactions, setReviewTransactions] = useState<any[]>([]);
  const [inReviewMode, setInReviewMode] = useState(false);

  const onboardingSteps: OnboardingStep[] = [
    {
      id: 'organization',
      title: 'Organization Setup',
      description: 'Set up your organization profile for Swiss accounting standards',
      icon: 'fa-building',
      completed: false,
      required: true
    },
    {
      id: 'mandate',
      title: 'Client Mandate',
      description: 'Configure client mandate and accounting requirements',
      icon: 'fa-handshake',
      completed: false,
      required: true
    },
    {
      id: 'chart-of-accounts',
      title: 'Chart of Accounts',
      description: 'Upload your existing chart of accounts or use Swiss standard',
      icon: 'fa-list-ol',
      completed: false,
      required: true
    },
    {
      id: 'historical-data',
      title: 'Historical Data',
      description: 'Upload previous transactions to establish patterns (optional)',
      icon: 'fa-database',
      completed: false,
      required: false
    },
    {
      id: 'rule-builder',
      title: 'AI Training - Rule Builder',
      description: 'Train your AI with first 50 transactions',
      icon: 'fa-brain',
      completed: false,
      required: true
    }
  ];

  const [steps, setSteps] = useState(onboardingSteps);

  const organizationComplete = useMemo(() =>
    Boolean(organizationData.name && organizationData.email && organizationData.businessType && organizationData.industry),
    [organizationData.name, organizationData.email, organizationData.businessType, organizationData.industry]
  );

  const mandateComplete = useMemo(() =>
    Boolean(mandateData.clientName && mandateData.accountingPeriod),
    [mandateData.clientName, mandateData.accountingPeriod]
  );

  const hasChartOfAccounts = useMemo(() => Boolean(chartOfAccountsFile), [chartOfAccountsFile]);
  const hasHistoricalData = useMemo(() => historicalDataFiles.length > 0, [historicalDataFiles]);

  const updateStepCompletion = useCallback(() => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => {
        switch (step.id) {
          case 'organization':
            return { ...step, completed: organizationComplete };
          case 'mandate':
            return { ...step, completed: mandateComplete };
          case 'chart-of-accounts':
            return { ...step, completed: hasChartOfAccounts };
          case 'historical-data':
            return { ...step, completed: hasHistoricalData };
          default:
            return step;
        }
      })
    );
  }, [organizationComplete, mandateComplete, hasChartOfAccounts, hasHistoricalData]);

  useEffect(() => {
    updateStepCompletion();
  }, [updateStepCompletion]);


  const canProceedToNext = () => {
    const currentStepData = steps[currentStep];
    if (!currentStepData) return false;

    // Last step (rule-builder) is always completable
    if (currentStep === steps.length - 1) return true;

    if (currentStepData.required && !currentStepData.completed) return false;
    return true;
  };

  const handleNextStep = async () => {
    if (currentStep === steps.length - 1) {
      // Final step - save all data and redirect to rule builder
      await saveOnboardingData();
      return;
    }

    if (canProceedToNext()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleAnalyzeHistoricalData = async (files: File[]) => {
    if (files.length === 0) return;
    
    setIsAnalyzing(true);
    setAnalysisDialogOpen(true);
    
    try {
      // Parse first file (for now, only analyze first file)
      const transactions = await PatternRecognitionService.parseHistoricalFile(files[0]);
      
      // Analyze patterns
      const result = await PatternRecognitionService.analyzeHistoricalData(transactions);
      
      setAnalysisResult(result);
    } catch (error) {
      console.error('Error analyzing historical data:', error);
      toast({
        title: 'Analysis Failed',
        description: error instanceof Error ? error.message : 'Could not analyze historical data',
        variant: 'destructive',
      });
      setAnalysisDialogOpen(false);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApproveRules = async () => {
    if (!analysisResult || !analysisResult.suggestedRules) return;
    
    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('No authenticated user');
      }

      // Get organization_id from users table
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', session.user.id)
        .single();

      if (!userData?.organization_id) {
        throw new Error('No organization found for user');
      }

      // Prepare rules for insertion
      const rulesToInsert = analysisResult.suggestedRules.map((rule: any) => ({
        organization_id: userData.organization_id,
        user_id: session.user.id,
        pattern_type: 'counterparty', // All historical rules are counterparty-based
        pattern_key: rule.patternKey.toLowerCase(),
        pattern_value: null,
        suggested_debit_account: rule.suggestedDebitAccount,
        suggested_credit_account: rule.suggestedCreditAccount,
        vat_code: rule.vatCode.toString(),
        vat_account: null,
        description_template: rule.descriptionTemplate,
        is_active: true,
        is_deprecated: false,
        scope: 'user', // User-specific rules from historical data
        base_confidence: rule.confidence / 100, // Convert to 0-1 scale
        current_confidence: rule.confidence / 100,
        usage_count: 0,
        success_count: 0,
        correction_count: 0,
        is_shared: false,
        learned_from_timestamp: new Date().toISOString(),
        semantic_threshold: 0.85,
        metadata: {
          source: 'historical_data_analysis',
          frequency: rule.frequency,
          reasoning: rule.reasoning,
        },
      }));

      // Insert rules into database
      const { data: insertedRules, error } = await supabase
        .from('learned_rules')
        .insert(rulesToInsert)
        .select();

      if (error) {
        console.error('Error saving rules:', error);
        throw error;
      }

      console.log(`✅ Successfully saved ${insertedRules?.length || 0} rules to database`);

      toast({
        title: 'Rules Saved!',
        description: `${insertedRules?.length || 0} classification rules have been created and are ready to use.`,
      });
    } catch (error) {
      console.error('Failed to save rules:', error);
      toast({
        title: 'Error Saving Rules',
        description: error instanceof Error ? error.message : 'Could not save rules to database',
        variant: 'destructive',
      });
    }
  };

  const handleUploadTransactions = async (file: File) => {
    try {
      // Parse the uploaded file
      const transactions = await PatternRecognitionService.parseHistoricalFile(file);
      
      // Take first 20 transactions for onboarding
      const reviewTxns = transactions.slice(0, 20);
      setReviewTransactions(reviewTxns);
      setInReviewMode(true);
      
      toast({
        title: 'File Uploaded',
        description: `${reviewTxns.length} transactions ready for review`,
      });
    } catch (error) {
      console.error('Error uploading transactions:', error);
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'Could not upload file',
        variant: 'destructive',
      });
    }
  };

  const handleReviewComplete = async (results: any[]) => {
    console.log('Review complete:', results);
    setInReviewMode(false);
    
    try {
      // Get current user and organization
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', session.user.id)
        .single();

      if (!userData?.organization_id) return;

      // Create rules from edited transactions
      const editedResults = results.filter(r => r.action === 'edited' && r.createRule);
      
      if (editedResults.length > 0) {
        const rulesToCreate = editedResults.map((result, index) => {
          const txn = reviewTransactions.find(t => t.id === result.transactionId);
          return {
            organization_id: userData.organization_id,
            user_id: session.user.id,
            pattern_type: 'counterparty',
            pattern_key: txn?.counterparty?.toLowerCase() || `manual-${index}`,
            pattern_value: null,
            suggested_debit_account: result.debitAccount,
            suggested_credit_account: result.creditAccount,
            vat_code: result.vatRate.toString(),
            vat_account: null,
            description_template: `${txn?.counterparty || 'Transaction'}`,
            is_active: true,
            is_deprecated: false,
            scope: 'user',
            base_confidence: 0.95, // High confidence from manual correction
            current_confidence: 0.95,
            usage_count: 0,
            success_count: 0,
            correction_count: 0,
            is_shared: false,
            learned_from_timestamp: new Date().toISOString(),
            semantic_threshold: 0.85,
            metadata: {
              source: 'onboarding_manual_correction',
              transaction_id: result.transactionId,
            },
          };
        });

        const { error } = await supabase
          .from('learned_rules')
          .insert(rulesToCreate);

        if (error) {
          console.error('Error creating rules from corrections:', error);
        } else {
          console.log(`✅ Created ${rulesToCreate.length} rules from manual corrections`);
        }
      }

      toast({
        title: 'Review Complete!',
        description: `${results.length} transactions reviewed, ${editedResults.length} rules created`,
      });
    } catch (error) {
      console.error('Error saving review results:', error);
      toast({
        title: 'Review Complete',
        description: `${results.length} transactions reviewed successfully`,
      });
    }
    
    // Continue to onboarding completion
  };

  const saveOnboardingData = async () => {
    setIsLoading(true);
    try {
      // Get current user from session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('No authenticated user');
      }
      const user = session.user;

      // 1. Check if user already has an organization, or create a new one
      const { data: currentUserDataArray } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id);

      const currentUserData = currentUserDataArray && currentUserDataArray.length > 0 
        ? currentUserDataArray[0] 
        : null;

      let orgData;

      if (currentUserData?.organization_id) {
        // User already has an organization, use it
        orgData = { id: currentUserData.organization_id };
      } else {
        // Create a new organization
        const slug = organizationData.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
          + '-' + Date.now();

        const { data: newOrgData, error: orgError } = await supabase
          .from('organizations')
          .insert({
            name: organizationData.name,
            slug: slug,
            subscription_tier: 'free',
            business_type: organizationData.businessType || 'sole_proprietorship',
            metadata: {
              industry: organizationData.industry,
              companySize: organizationData.companySize,
              canton: organizationData.canton,
              address: organizationData.address,
              phone: organizationData.phone,
              vatNumber: organizationData.vatNumber,
            }
          })
          .select()
          .maybeSingle();

        if (orgError) {
          console.error('Error saving organization:', orgError);
          throw orgError;
        }

        if (!newOrgData) {
          throw new Error('Organization creation returned no data');
        }

        orgData = newOrgData;
      }

      // 2. Create or update user profile with organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          email: user.email,
          organization_id: orgData.id,
          first_name: user.user_metadata?.first_name || '',
          last_name: user.user_metadata?.last_name || '',
          role: user.user_metadata?.role || 'partner', // Use role from signup or default to partner level
        })
        .select()
        .maybeSingle();

      if (profileError) {
        console.error('Error updating user profile:', profileError);
        throw profileError;
      }

      if (!profileData) {
        throw new Error('Profile update returned no data');
      }

      // FIX: Verify user profile is committed before proceeding
      // This prevents RLS policy failures during Chart of Accounts import
      const { data: verifiedProfile } = await supabase
        .from('users')
        .select('id, organization_id')
        .eq('id', user.id)
        .single();

      if (!verifiedProfile || !verifiedProfile.organization_id) {
        throw new Error('User profile verification failed - please try again');
      }

      console.log('✅ User profile verified:', verifiedProfile);

      // 3. Create mandate with all required fields
      const { data: mandateDataResult, error: mandateError } = await supabase
        .from('mandates')
        .insert({
          organization_id: orgData.id,
          client_name: mandateData.clientName || 'Default Mandate',
          mandate_type: 'full_bookkeeping',
          vat_liable: true,
          vat_method: 'effective',
          status: 'active',
        })
        .select()
        .maybeSingle();

      if (mandateError) {
        console.error('Error saving mandate:', mandateError);
        alert(`Mandate creation failed: ${mandateError.message || JSON.stringify(mandateError)}`);
        throw mandateError;
      }

      if (!mandateDataResult) {
        console.error('No mandate data returned after insert');
        throw new Error('Mandate creation returned no data');
      }

      // 4. Process Chart of Accounts if uploaded (not Swiss standard)
      if (chartOfAccountsFile && chartOfAccountsFile.name !== 'swiss-standard-coa.txt') {
        try {
          const fileContent = await chartOfAccountsFile.text();
          const lines = fileContent.split('\n').slice(2); // Skip title row and header

          // Fetch existing accounts to check for duplicates
          const { data: existingAccounts } = await supabase
            .from('mandate_chart_of_accounts')
            .select('account_number')
            .eq('organization_id', orgData.id)
            .eq('mandate_id', mandateDataResult.id);

          const existingNumbers = new Set(existingAccounts?.map(a => a.account_number) || []);

          // Parse CSV with format: Nummer*,Name*,Gruppe*,Kontoart*,Systemkonto,Verknüpftes Konto,MWST-Typ
          const importedAccounts = lines
            .filter(line => line.trim())
            .map(line => {
              // Handle quoted fields properly
              const fields: string[] = [];
              let currentField = '';
              let inQuotes = false;

              for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                  inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                  fields.push(currentField.trim());
                  currentField = '';
                } else {
                  currentField += char;
                }
              }
              fields.push(currentField.trim());

              const [number, name, gruppe, kontoart, systemkonto, verknuepft, mwstTyp] = fields;

              // Skip if no account number or if it's a group/header row
              if (!number || kontoart?.toLowerCase() === 'gruppe') {
                return null;
              }

              // Determine account class from the first digit (must be 1-8 per DB constraint)
              const firstDigit = parseInt(number.charAt(0));
              // Swiss COA mapping: 0→1 (balance sheet), 1-8→same, 9→8 (closing accounts)
              const accountClass = firstDigit === 0 ? 1 : 
                                   firstDigit >= 1 && firstDigit <= 8 ? firstDigit : 
                                   firstDigit === 9 ? 8 : 1;

              // Determine if VAT applicable
              const hasVAT = mwstTyp?.toLowerCase().includes('tax') ||
                            verknuepft?.toLowerCase() === 'ja' ||
                            mwstTyp?.toLowerCase().includes('sales') ||
                            mwstTyp?.toLowerCase().includes('pre');

              return {
                account_number: number,
                account_name: name.replace(/"/g, ''),
                account_class: accountClass,
                account_type: kontoart?.toLowerCase() === 'aktiv' ? 'asset' :
                             kontoart?.toLowerCase() === 'passiv' ? 'liability' :
                             kontoart?.toLowerCase() === 'ertrag' ? 'revenue' :
                             kontoart?.toLowerCase() === 'aufwand' ? 'expense' : 'operating',
                swiss_vat_applicable: hasVAT,
                is_active: true,
                organization_id: orgData.id,
                mandate_id: mandateDataResult.id
              };
            })
            .filter((acc): acc is NonNullable<typeof acc> => acc !== null)
            .filter(acc => !existingNumbers.has(acc.account_number)); // Only new accounts

          if (importedAccounts.length > 0) {
            console.log(`📊 Attempting to import ${importedAccounts.length} accounts...`);
            console.log('Import context:', {
              organizationId: orgData.id,
              mandateId: mandateDataResult.id,
              userId: user.id,
              sampleAccount: importedAccounts[0]
            });

            // Insert new accounts - no silent failure!
            const { data: insertedAccounts, error: accountsError } = await supabase
              .from('mandate_chart_of_accounts')
              .insert(importedAccounts)
              .select();

            if (accountsError) {
              console.error('❌ Chart of Accounts Import Failed:', accountsError);
              toast({
                title: "Fehler beim Import des Kontenplans",
                description: `Der Kontenplan konnte nicht importiert werden: ${accountsError.message}`,
                variant: "destructive",
              });
              throw new Error(`COA Import failed: ${accountsError.message}`);
            }

            console.log(`✅ Successfully imported ${insertedAccounts?.length || 0} accounts from Chart of Accounts`);
            toast({
              title: "Kontenplan erfolgreich importiert",
              description: `${insertedAccounts?.length || 0} Konten wurden hinzugefügt.`,
            });
          } else {
            console.log('ℹ️ No new accounts to import (all accounts already exist)');
          }
        } catch (coaError) {
          // Re-throw COA errors so they're visible to the user
          console.error('❌ Critical error processing Chart of Accounts:', coaError);
          throw coaError;
        }
      }

      // 5. Upload historical data files if any were selected
      if (historicalDataFiles.length > 0) {
        for (const file of historicalDataFiles) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('mandateId', mandateDataResult.id);
            formData.append('organizationId', orgData.id);

            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
              throw new Error('Keine aktive Sitzung für Datei-Upload verfügbar');
            }

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/file-upload`, {
              method: 'POST',
              body: formData,
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
            });

            const payload: UploadFunctionResponse | undefined = await response.json().catch(() => undefined);

            if (!response.ok || payload?.success === false) {
              const message = payload?.error || payload?.message || `Upload failed (${response.status})`;
              console.error('Error uploading file:', file.name, message);
            }
          } catch (fileError) {
            console.error('Error processing file:', file.name, fileError);
          }
        }
      }

      // Mark onboarding as completed
      localStorage.setItem('onboarding_completed', 'true');

      // Redirect to dashboard with hard reload to ensure fresh data
      window.location.replace('/');
    } catch (error) {
      console.error('❌ Error saving onboarding data:', error);
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: string }).message)
          : JSON.stringify(error);
      alert(`Failed to save onboarding data: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    const step = steps[currentStep];

    switch (step?.id) {
      case 'organization':
        return <OrganizationStep data={organizationData} setData={setOrganizationData} />;
      case 'mandate':
        return <MandateStep data={mandateData} setData={setMandateData} />;
      case 'chart-of-accounts':
        return <ChartOfAccountsStep file={chartOfAccountsFile} setFile={setChartOfAccountsFile} />;
      case 'historical-data':
        return (
          <HistoricalDataStep
            files={historicalDataFiles}
            setFiles={setHistoricalDataFiles}
            onAnalyze={handleAnalyzeHistoricalData}
            analysisResult={analysisResult}
          />
        );
      case 'rule-builder':
        return (
          <RuleBuilderStep
            onUploadTransactions={handleUploadTransactions}
            hasHistoricalData={historicalDataFiles.length > 0}
          />
        );
      default:
        return null;
    }
  };

  const completedSteps = steps.filter(step => step.completed).length;
  const progress = (completedSteps / steps.length) * 100;

  return (
    <div className="min-h-screen bg-brand-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-header-mb">
          <h1 className="text-3xl font-space-grotesk font-bold text-brand-primary mb-2">
            Welcome to Swiss Finance OS
          </h1>
          <p className="text-lg text-brand-text-secondary">
            Let's set up your AI-powered accounting system step by step
          </p>
        </div>

        {/* Progress Bar */}
        <Card className="mb-header-mb">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-brand-primary">Setup Progress</span>
              <span className="text-sm text-brand-text-secondary">{completedSteps} of {steps.length} completed</span>
            </div>
            <Progress value={progress} className="w-full h-2 mb-6" />

            {/* Step Indicators */}
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex flex-col items-center cursor-pointer transition-all ${
                    index === currentStep
                      ? 'text-brand-primary'
                      : step.completed
                        ? 'text-green-600'
                        : 'text-brand-text-secondary'
                  }`}
                  onClick={() => setCurrentStep(index)}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                    index === currentStep
                      ? 'bg-brand-primary text-white'
                      : step.completed
                        ? 'bg-green-500 text-white'
                        : 'bg-brand-secondary text-brand-text-secondary'
                  }`}>
                    <i className={`fa-solid ${step.completed ? 'fa-check' : step.icon} text-sm`}></i>
                  </div>
                  <span className="text-xs text-center font-medium max-w-20">{step.title}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Current Step Content */}
        <Card className="mb-header-mb">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-brand-primary rounded-xl flex items-center justify-center">
                <i className={`fa-solid ${steps[currentStep]?.icon} text-white text-lg`}></i>
              </div>
              <div>
                <CardTitle className="text-xl font-space-grotesk text-brand-primary">
                  {steps[currentStep]?.title}
                </CardTitle>
                <CardDescription className="text-brand-text-secondary">
                  {steps[currentStep]?.description}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePreviousStep}
            disabled={currentStep === 0 || isLoading}
            className="px-6"
          >
            <i className="fa-solid fa-arrow-left mr-2"></i>
            Previous
          </Button>

          <Button
            onClick={handleNextStep}
            disabled={!canProceedToNext() || isLoading}
            className="px-6 bg-brand-primary hover:bg-brand-primary/90"
          >
            {isLoading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                Saving...
              </>
            ) : currentStep === steps.length - 1 ? (
              <>
                Complete Setup
                <i className="fa-solid fa-rocket ml-2"></i>
              </>
            ) : (
              <>
                Next
                <i className="fa-solid fa-arrow-right ml-2"></i>
              </>
            )}
          </Button>
        </div>

        {/* Historical Analysis Dialog */}
        <HistoricalAnalysisDialog
          open={analysisDialogOpen}
          onOpenChange={setAnalysisDialogOpen}
          analysisResult={analysisResult}
          isAnalyzing={isAnalyzing}
          onApproveRules={handleApproveRules}
        />

        {/* Focused Review Mode */}
        {inReviewMode && reviewTransactions.length > 0 && (
          <FocusedReviewMode
            transactions={reviewTransactions}
            isOnboarding={true}
            hasHistoricalData={historicalDataFiles.length > 0}
            onComplete={handleReviewComplete}
            onExit={() => setInReviewMode(false)}
          />
        )}
      </div>
    </div>
  );
};

// Step Components
interface OrganizationStepProps {
  data: OrganizationData;
  setData: (data: OrganizationData) => void;
}

const OrganizationStep: React.FC<OrganizationStepProps> = ({ data, setData }) => {
  const updateField = (field: keyof OrganizationData, value: string) => {
    setData({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="orgName">Organization Name *</Label>
          <Input
            id="orgName"
            value={data.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Your Company Name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="orgEmail">Email Address *</Label>
          <Input
            id="orgEmail"
            type="email"
            value={data.email}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder="contact@company.ch"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="orgPhone">Phone Number</Label>
          <Input
            id="orgPhone"
            value={data.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder="+41 XX XXX XX XX"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="vatNumber">VAT Number</Label>
          <Input
            id="vatNumber"
            value={data.vatNumber}
            onChange={(e) => updateField('vatNumber', e.target.value)}
            placeholder="CHE-XXX.XXX.XXX"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="businessType">Business Type *</Label>
          <Select value={data.businessType} onValueChange={(value) => updateField('businessType', value)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select business type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
              <SelectItem value="partnership">Partnership</SelectItem>
              <SelectItem value="ltd">Limited Company (GmbH)</SelectItem>
              <SelectItem value="corporation">Corporation (AG)</SelectItem>
              <SelectItem value="association">Association</SelectItem>
              <SelectItem value="foundation">Foundation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="industry">Industry *</Label>
          <Select value={data.industry || ''} onValueChange={(value) => updateField('industry', value)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select your industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="consulting">Consulting & Advisory</SelectItem>
              <SelectItem value="it_services">IT Services & Software</SelectItem>
              <SelectItem value="retail">Retail & E-commerce</SelectItem>
              <SelectItem value="construction">Construction & Engineering</SelectItem>
              <SelectItem value="hospitality">Hospitality & Gastronomy</SelectItem>
              <SelectItem value="healthcare">Healthcare & Medical</SelectItem>
              <SelectItem value="manufacturing">Manufacturing & Production</SelectItem>
              <SelectItem value="real_estate">Real Estate & Property</SelectItem>
              <SelectItem value="professional_services">Professional Services</SelectItem>
              <SelectItem value="creative">Creative & Media</SelectItem>
              <SelectItem value="transport">Transport & Logistics</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-brand-text-secondary">
            We'll suggest common accounts for your industry
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="companySize">Company Size</Label>
          <Select value={data.companySize || ''} onValueChange={(value) => updateField('companySize', value)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="micro">1-9 employees (Micro)</SelectItem>
              <SelectItem value="small">10-49 employees (Small)</SelectItem>
              <SelectItem value="medium">50-249 employees (Medium)</SelectItem>
              <SelectItem value="large">250+ employees (Large)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="canton">Canton</Label>
          <Select value={data.canton || ''} onValueChange={(value) => updateField('canton', value)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select canton" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="zh">Zürich</SelectItem>
              <SelectItem value="be">Bern</SelectItem>
              <SelectItem value="lu">Luzern</SelectItem>
              <SelectItem value="ur">Uri</SelectItem>
              <SelectItem value="sz">Schwyz</SelectItem>
              <SelectItem value="ow">Obwalden</SelectItem>
              <SelectItem value="nw">Nidwalden</SelectItem>
              <SelectItem value="gl">Glarus</SelectItem>
              <SelectItem value="zg">Zug</SelectItem>
              <SelectItem value="fr">Fribourg</SelectItem>
              <SelectItem value="so">Solothurn</SelectItem>
              <SelectItem value="bs">Basel-Stadt</SelectItem>
              <SelectItem value="bl">Basel-Landschaft</SelectItem>
              <SelectItem value="sh">Schaffhausen</SelectItem>
              <SelectItem value="ar">Appenzell Ausserrhoden</SelectItem>
              <SelectItem value="ai">Appenzell Innerrhoden</SelectItem>
              <SelectItem value="sg">St. Gallen</SelectItem>
              <SelectItem value="gr">Graubünden</SelectItem>
              <SelectItem value="ag">Aargau</SelectItem>
              <SelectItem value="tg">Thurgau</SelectItem>
              <SelectItem value="ti">Ticino</SelectItem>
              <SelectItem value="vd">Vaud</SelectItem>
              <SelectItem value="vs">Valais</SelectItem>
              <SelectItem value="ne">Neuchâtel</SelectItem>
              <SelectItem value="ge">Genève</SelectItem>
              <SelectItem value="ju">Jura</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-brand-text-secondary">
            For VAT and compliance settings
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="orgAddress">Business Address</Label>
        <Textarea
          id="orgAddress"
          value={data.address}
          onChange={(e) => updateField('address', e.target.value)}
          placeholder="Street, City, Postal Code, Switzerland"
          rows={3}
        />
      </div>
    </div>
  );
};

interface MandateStepProps {
  data: MandateData;
  setData: (data: MandateData) => void;
}

const MandateStep: React.FC<MandateStepProps> = ({ data, setData }) => {
  const updateField = (field: keyof MandateData, value: string) => {
    setData({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="clientName">Client/Mandate Name *</Label>
          <Input
            id="clientName"
            value={data.clientName}
            onChange={(e) => updateField('clientName', e.target.value)}
            placeholder="Client Company Name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="accountingPeriod">Accounting Period *</Label>
          <Select value={data.accountingPeriod} onValueChange={(value) => updateField('accountingPeriod', value)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select accounting period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="vatReporting">VAT Reporting</Label>
          <Select value={data.vatReporting} onValueChange={(value) => updateField('vatReporting', value)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select VAT reporting period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
              <SelectItem value="none">Not VAT registered</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="clientAddress">Client Address</Label>
        <Textarea
          id="clientAddress"
          value={data.clientAddress}
          onChange={(e) => updateField('clientAddress', e.target.value)}
          placeholder="Client business address"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="specialRequirements">Special Requirements</Label>
        <Textarea
          id="specialRequirements"
          value={data.specialRequirements}
          onChange={(e) => updateField('specialRequirements', e.target.value)}
          placeholder="Any special accounting requirements, industry-specific rules, etc."
          rows={4}
        />
      </div>
    </div>
  );
};

interface ChartOfAccountsStepProps {
  file: File | null;
  setFile: (file: File | null) => void;
}

const ChartOfAccountsStep: React.FC<ChartOfAccountsStepProps> = ({ file, setFile }) => {
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
    }
  };

  const useSwissStandard = () => {
    // Create a placeholder file to indicate Swiss standard selection
    const blob = new Blob(['Swiss Standard Chart of Accounts'], { type: 'text/plain' });
    const standardFile = new File([blob], 'swiss-standard-coa.txt', { type: 'text/plain' });
    setFile(standardFile);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upload Option */}
        <Card className="border-2 border-dashed border-brand-border hover:border-brand-primary transition-colors">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-brand-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-upload text-brand-primary text-2xl"></i>
            </div>
            <h3 className="font-semibold text-brand-primary mb-2">Upload Existing Chart</h3>
            <p className="text-sm text-brand-text-secondary mb-4">
              Upload your existing chart of accounts (CSV, Excel, or PDF)
            </p>
            <Input
              type="file"
              accept=".csv,.xlsx,.xls,.pdf"
              onChange={handleFileUpload}
              className="mb-4"
            />
            {file && file.name !== 'swiss-standard-coa.txt' && (
              <div className="flex items-center text-green-600 text-sm">
                <i className="fa-solid fa-check-circle mr-2"></i>
                {file.name}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Swiss Standard Option */}
        <Card
          className={`border-2 cursor-pointer transition-colors ${
            file?.name === 'swiss-standard-coa.txt'
              ? 'border-brand-primary bg-brand-secondary'
              : 'border-brand-border hover:border-brand-primary'
          }`}
          onClick={useSwissStandard}
        >
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-flag text-red-600 text-2xl"></i>
            </div>
            <h3 className="font-semibold text-brand-primary mb-2">Swiss Standard</h3>
            <p className="text-sm text-brand-text-secondary mb-4">
              Use the standard Swiss chart of accounts (KMU model)
            </p>
            <Button
              variant={file?.name === 'swiss-standard-coa.txt' ? 'default' : 'outline'}
              size="sm"
            >
              <i className="fa-solid fa-list-ol mr-2"></i>
              Use Swiss Standard
            </Button>
            {file?.name === 'swiss-standard-coa.txt' && (
              <div className="flex items-center justify-center text-green-600 text-sm mt-2">
                <i className="fa-solid fa-check-circle mr-2"></i>
                Selected
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <i className="fa-solid fa-info-circle text-blue-600 mr-3 mt-0.5"></i>
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Chart of Accounts Purpose</h4>
            <p className="text-sm text-blue-800">
              Your chart of accounts defines how transactions are categorized. The AI will learn to
              automatically classify transactions into these accounts based on your manual training.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface HistoricalDataStepProps {
  files: File[];
  setFiles: (files: File[]) => void;
  onAnalyze: (files: File[]) => Promise<void>;
  analysisResult: any;
}

const HistoricalDataStep: React.FC<HistoricalDataStepProps> = ({ files, setFiles, onAnalyze, analysisResult }) => {
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(e.target.files || []);
    setFiles([...files, ...uploadedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleAnalyze = () => {
    if (files.length > 0) {
      onAnalyze(files);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-2 border-dashed border-brand-border rounded-lg p-8 text-center">
        <div className="w-16 h-16 bg-brand-secondary rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fa-solid fa-database text-brand-primary text-2xl"></i>
        </div>
        <h3 className="font-semibold text-brand-primary mb-2">Upload Historical Data</h3>
        <p className="text-sm text-brand-text-secondary mb-4">
          Upload previous bank statements, invoices, or transaction exports to help the AI learn patterns
        </p>
        <Input
          type="file"
          multiple
          accept=".csv,.xlsx,.xls,.pdf,.xml"
          onChange={handleFileUpload}
          className="max-w-md mx-auto"
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-brand-primary">Uploaded Files:</h4>
            <Button
              onClick={handleAnalyze}
              size="sm"
              className="bg-brand-primary hover:bg-brand-primary/90"
            >
              <i className="fa-solid fa-brain mr-2"></i>
              Analyze Patterns
            </Button>
          </div>
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between bg-brand-secondary p-3 rounded-lg">
              <div className="flex items-center">
                <i className="fa-solid fa-file text-brand-primary mr-3"></i>
                <span className="text-sm">{file.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFile(index)}
                className="text-red-600 hover:text-red-700"
              >
                <i className="fa-solid fa-trash"></i>
              </Button>
            </div>
          ))}
        </div>
      )}

      {analysisResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <i className="fa-solid fa-check-circle text-green-600 mr-3 mt-0.5"></i>
            <div className="flex-1">
              <h4 className="font-medium text-green-900 mb-2">Analysis Complete!</h4>
              <div className="grid grid-cols-2 gap-4 text-sm text-green-800">
                <div>
                  <strong>{analysisResult.patternsFound}</strong> patterns identified
                </div>
                <div>
                  <strong>{analysisResult.rulesGenerated}</strong> rules generated
                </div>
                <div>
                  <strong>{analysisResult.avgConfidence.toFixed(0)}%</strong> avg confidence
                </div>
                <div>
                  <strong>{analysisResult.analyzedTransactions}</strong> transactions analyzed
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <i className="fa-solid fa-lightbulb text-yellow-600 mr-3 mt-0.5"></i>
          <div>
            <h4 className="font-medium text-yellow-900 mb-1">Optional but Recommended</h4>
            <p className="text-sm text-yellow-800">
              Historical data helps the AI understand your business patterns and improves initial
              classification accuracy. You can skip this step and train the AI with new transactions instead.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface RuleBuilderStepProps {
  onUploadTransactions: (file: File) => Promise<void>;
  hasHistoricalData: boolean;
}

const RuleBuilderStep: React.FC<RuleBuilderStepProps> = ({ onUploadTransactions, hasHistoricalData }) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      await onUploadTransactions(file);
    }
  };

  return (
    <div className="space-y-6 text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-brand-primary to-brand-accent rounded-full flex items-center justify-center mx-auto">
        <i className="fa-solid fa-brain text-white text-3xl"></i>
      </div>

      <div>
        <h3 className="text-2xl font-space-grotesk font-bold text-brand-primary mb-2">
          {hasHistoricalData ? 'AI Pre-Trained!' : 'AI Training Time!'}
        </h3>
        <p className="text-brand-text-secondary">
          {hasHistoricalData
            ? 'Your AI has learned from historical data. Upload transactions to verify and fine-tune.'
            : 'Upload your first transactions and we\'ll review them together to train your AI.'}
        </p>
      </div>

      {hasHistoricalData && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
          <div className="flex items-center justify-center gap-2 text-green-700">
            <i className="fa-solid fa-check-circle"></i>
            <span className="text-sm font-medium">
              AI accuracy: ~96% (from historical patterns)
            </span>
          </div>
        </div>
      )}

      {!uploadedFile ? (
        <div className="border-2 border-dashed border-brand-border rounded-lg p-8 max-w-md mx-auto">
          <div className="w-16 h-16 bg-brand-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-upload text-brand-primary text-2xl"></i>
          </div>
          <h4 className="font-semibold text-brand-primary mb-2">Upload Transactions</h4>
          <p className="text-sm text-brand-text-secondary mb-4">
            Upload a bank export (CSV, Excel, or CAMT.053) to start the review process
          </p>
          <Input
            type="file"
            accept=".csv,.xlsx,.xls,.xml"
            onChange={handleFileUpload}
            className="max-w-sm mx-auto"
          />
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
          <div className="flex items-center justify-center gap-2 text-blue-700">
            <i className="fa-solid fa-file-check"></i>
            <span className="text-sm">
              {uploadedFile.name} uploaded - Starting review...
            </span>
          </div>
        </div>
      )}

      <div className="bg-brand-secondary border border-brand-primary rounded-lg p-6 max-w-2xl mx-auto">
        <h4 className="font-semibold text-brand-primary mb-3">What happens next:</h4>
        <div className="space-y-3 text-sm text-left">
          <div className="flex items-start">
            <i className="fa-solid fa-eye text-brand-primary mr-3 mt-0.5"></i>
            <span>Review first 20 transactions in focused mode</span>
          </div>
          <div className="flex items-start">
            <i className="fa-solid fa-brain text-brand-primary mr-3 mt-0.5"></i>
            <span>AI shows suggestions with confidence scores</span>
          </div>
          <div className="flex items-start">
            <i className="fa-solid fa-wand-magic-sparkles text-brand-primary mr-3 mt-0.5"></i>
            <span>Approve good suggestions, edit incorrect ones</span>
          </div>
          <div className="flex items-start">
            <i className="fa-solid fa-graduation-cap text-brand-primary mr-3 mt-0.5"></i>
            <span>AI learns from corrections and improves accuracy</span>
          </div>
          <div className="flex items-start">
            <i className="fa-solid fa-rocket text-brand-primary mr-3 mt-0.5"></i>
            <span>Complete setup and start using Amplio Finance!</span>
          </div>
        </div>
      </div>
    </div>
  );
};
